import { runInAction } from 'mobx';
import React, { useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { basedAxios as Axios, useStore } from '../../store/Store';

// Label groups being fetched right now, shared by <EventLabel> and GetEventLabelValueFn: however many components ask
// for the same group while it loads (one FavoriteButton per row of a list, say), only one request is sent.
const groupsInFlight = new Set();
const flightKey = (eventId, groupName, lang) => `${eventId}|${groupName}|${lang}`;

const EventLabel = observer(({ name, groupName, textOnly = false, languageCode = null, evId = null, defaultValue = null }) => {
  const store = useStore();
  const eventId = evId || store?.environment?.eventId;
  const lang = languageCode || store?.environment?.lang;

  groupName = groupName.toUpperCase();
  const label = useMemo(() => {
    return store.eventLabels?.[groupName]?.[name.toUpperCase()] || null;
  }, [store.eventLabels?.[groupName], groupName, name]);

  useEffect(() => {
    if (typeof store.eventLabels[groupName] !== 'object' && store.eventLabels[groupName] !== 'Loading') {
      getGroup();
    } else if (label && label.languageCode != lang) {
      getGroup();
    }
  }, [store.eventLabels[groupName], label, lang]);

  const getGroup = useCallback(() => {
    const key = flightKey(eventId, groupName, lang);
    if (groupsInFlight.has(key)) return;
    groupsInFlight.add(key);
    runInAction(() => {
      store.eventLabels[groupName] = 'Loading';
    });
    Axios.get(`./eventLabel/getGroup/${eventId}?groupName=${groupName}&languageCode=${lang}`)
      .then(({ data }) => {
        runInAction(() => {
          const groups = { ...store.eventLabels };
          const groupData = {};
          data.map(d => {
            groupData[d.name.toUpperCase()] = d;
          });
          groups[groupName.toUpperCase()] = groupData;
          store.eventLabels = groups;
        });
      })
      .finally(() => groupsInFlight.delete(key));
  });

  // When a label hasn't been seeded yet, fall back to defaultValue (if given) instead of #name#.
  const fallback = defaultValue != null ? defaultValue : `#${name}#`;
  if (textOnly) return label && label !== 'Loading' ? label.value : fallback;
  if (!label || label === 'Loading') return <span>{fallback}</span>;
  return <span style={{ alignItems: 'center' }}>{label.value}</span>;
});

export default EventLabel;

export const GetEventLabelValueFn = ({ name = '', groupName = '' }, eventLabels, environment, updateEventLabels) => {
  const { eventId, lang } = environment;
  // Groups are stored (and asked for) in upper case, as <EventLabel> does, so both share the same entry and request.
  const group = groupName.toUpperCase();
  const label = eventLabels[group] ? eventLabels[group][name.toUpperCase()] : null;
  if (typeof eventLabels[group] !== 'object' && eventLabels[group] !== 'Loading') {
    getGroup();
  } else if (label && label.languageCode != lang) {
    getGroup();
  }
  function getGroup() {
    // This runs while a component renders, once per component using the label: the in-flight set is what keeps a
    // list of them down to one request. (No 'Loading' marker is written here - that would change, mid-render, a
    // store value other components are watching.)
    const key = flightKey(eventId, group, lang);
    if (groupsInFlight.has(key)) return;
    groupsInFlight.add(key);
    Axios.get(`./eventLabel/getGroup/${eventId}?groupName=${group}&languageCode=${lang}`)
      .then(({ data }) => {
        const groupData = {};
        data.map(d => {
          groupData[d.name.toUpperCase()] = d;
        });
        if (typeof updateEventLabels === 'function') {
          updateEventLabels(group, groupData);
        }
      })
      .finally(() => groupsInFlight.delete(key));
  }
  return label && label !== 'Loading' ? label.value : `#${name}#`;
};
